import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  BadgeInfo,
  CalendarDays,
  ChevronLeft,
  MapPin,
  Phone,
  User,
  Users,
} from "lucide-react";
import Footer from "./Footer";
import NavBar from "./NavBar";
import Loading from "./Loading";
import SubscriptionStatusCard from "../components/SubscriptionStatusCard";
import useCurrentUsuario from "../hooks/useCurrentUsuario";
import api, { authHeaders } from "../services/api";

function MinhaConta() {
  const [detalhe, setDetalhe] = useState({});
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const navigate = useNavigate();
  const { usuario, loading: loadingUsuario } = useCurrentUsuario();

  function formatarData(dataISO) {
    if (!dataISO) return "dados indisponiveis";

    const data = new Date(dataISO);

    if (Number.isNaN(data.getTime())) return "dados indisponiveis";

    return new Intl.DateTimeFormat("pt-MZ", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(data);
  }

  async function mostrarDetalhes() {
    try {
      setLoadingDetalhe(true);
      const token = localStorage.getItem("token");

      if (!token) return;

      const dadosDoToken = jwtDecode(token);

      if (!dadosDoToken?.idUsuario) return;

      const resposta = await api.get(`/detalheusuario/${dadosDoToken.idUsuario}`, {
        headers: authHeaders(token),
      });

      setDetalhe(resposta.data?.detalhe || {});
    } catch (erro) {
      console.log(erro);
      setDetalhe({});
    } finally {
      setLoadingDetalhe(false);
    }
  }

  useEffect(() => {
    mostrarDetalhes();
  }, []);

  const dadosPerfil = [
    {
      label: "Nome",
      value: detalhe?.nome || usuario?.name || "dados indisponiveis",
      icon: User,
    },
    {
      label: "Endereco",
      value: detalhe?.endereco || "dados indisponiveis",
      icon: MapPin,
    },
    {
      label: "Contacto",
      value: detalhe?.contacto || "dados indisponiveis",
      icon: Phone,
    },
    {
      label: "NUIT",
      value: detalhe?.nuit || "dados indisponiveis",
      icon: BadgeInfo,
    },
    {
      label: "Data da subscricao",
      value: formatarData(detalhe?.dataSubscricao),
      icon: CalendarDays,
    },
    {
      label: "Colaborador associado",
      value: detalhe?.colaboradorAssociado || "dados indisponiveis",
      icon: Users,
    },
  ];

  const loading = loadingDetalhe || loadingUsuario;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 font-questrial">
      <div className="bg-blue-500 text-gray-200">
        <NavBar />
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loading />
        </div>
      ) : (
        <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-6 px-4 py-6">
          <button
            className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-slate-600 transition hover:text-blue-600"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft size={18} />
            Voltar
          </button>

          <SubscriptionStatusCard
            usuario={usuario}
            onRenew={() => navigate("/planos")}
            onReceipts={() => navigate("/recibos")}
          />

          <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-slate-100 p-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                <User size={24} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Dados do cliente
                </p>
                <h2 className="text-lg font-semibold text-slate-900">
                  Minha Conta
                </h2>
              </div>
            </div>

            <div className="grid gap-4 p-4 sm:grid-cols-2">
              {dadosPerfil.map(({ label, value, icon: Icon }) => (
                <div
                  key={label}
                  className="flex items-start gap-3 rounded-md border border-slate-100 bg-slate-50 p-3"
                >
                  <Icon className="mt-1 shrink-0 text-blue-600" size={18} />
                  <div>
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="mt-1 break-words font-semibold text-slate-800">
                      {value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>
      )}

      <div className="bg-blue-500 text-gray-200">
        <Footer />
      </div>
    </div>
  );
}

export default MinhaConta;
